import React, { useCallback, useEffect, useState } from "react";

import { CheckCircle, ContentCopy, DoNotDisturbOn } from "@mui/icons-material";
import {
  Table,
  TableCell,
  TableHead,
  TableRow,
  TableBody,
  TableContainer,
  Paper,
  IconButton,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from "@mui/material";
import { useHistory } from "react-router-dom";

import styles from "./UsersQuizTable.module.css";

import Papa from "papaparse";
import { saveAs } from "file-saver";
import ControlledCheckboxRealEmail from "../../ControlledCheckboxRealEmail";
import { splitName } from "../../../utils/Constants";
import EditUserButtonAlert from "../../EditUserButtonAlert";
import firebase, { db, functions } from "../../../utils/firebase";
import AddBusinessButtonAlert from "./AddBusinessButtonAlert";
import {
  getTotalMembersWithUsage,
  updateUserTeamsUsage,
} from "./updateUserTeamsUsage";

export default function UsersQuizTable() {
  const [showCopySuccess, setShowCopySuccess] = useState(false);
  const [showSubscriptions, setShowSubscriptions] = useState(false);

  const [users, setUsers] = useState([]);
  const [businessData, setBusinessData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [emailOpenStatus, setEmailOpenStatus] = useState({});

  const [isDownloadingCSV, setIsDownloadingCSV] = useState(false);

  const [lastVisible, setLastVisible] = useState();
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const history = useHistory();

  const headers = [
    "Started a Quiz",
    "Edit",
    "First name",
    "Last name",
    "Email",
    "$ Value",
    "POC",
    "State",
    "District",
    "Answered",
    "Admin/POC",
    "Admin email",
    "First contact for renewal",
    "District/Individual teacher plan",
    "#schools",
    "2023-2024 sub type",
    "2023-2024 payment type",
    "2023-2024 recurrence",
    "2023-2024 amnt paid",
    "2023-2024 exprity date",
    "Renewed?",
    "2024-2025 sub type",
    "2024-2025 payment type",
    "2024-2025 recurrence",
    "2024-2025 amnt paid",
    "2024-2025 exprity date",
    "From Siena",
    "Notes",
    "Email bounced",
    "Never bounce",
    "Has Created Team",
    "Has Created Team Project",
    "Teams Students Added",
    "Teams Replit Imports",
    "Teams Codingrooms Imports",
    "Teams Templates Used",
    "Teams Templates",
    "Active in Teams",
    "Active curriculum",
    "Total Quiz Games started",
    "Total Custom Quiz Games Started",
    "Total Assigned Quiz Games Started",
    "Quizzes Started Names",
    "Quizzes Started Courses",
    "Clicked For More Info",
    "Clicked For More Info ProjectId",
    "Last Seen",
    "Subscription",
    "Opened by Ryan",
    "Never contacted",
  ];

  // Utility function to parse file systems
  const parseFileSystem = (fileSystem) => {
    if (!fileSystem) return [];

    return typeof fileSystem === "string" ? JSON.parse(fileSystem) : fileSystem;
  };

  // Utility function to format timestamps
  const formatTimestamp = (timestamp, isMilliseconds = false) => {
    if (!timestamp) return "-";

    let milliseconds;

    if (typeof timestamp === "number") {
      // Handle plain number timestamp
      milliseconds = timestamp;
    } else if (typeof timestamp === "object" && timestamp.seconds) {
      // Handle Firestore timestamp
      milliseconds =
        timestamp.seconds * 1000 + Math.floor(timestamp.nanoseconds / 1e6);
    } else {
      return "-";
    }

    try {
      const date = new Date(milliseconds);

      if (isNaN(date.getTime())) {
        return "-";
      }

      const formattedDate = `${(date.getMonth() + 1)
        .toString()
        .padStart(2, "0")}/${date
        .getDate()
        .toString()
        .padStart(2, "0")}/${date.getFullYear()}`;

      const formattedTime = `${date
        .getHours()
        .toString()
        .padStart(2, "0")}:${date
        .getMinutes()
        .toString()
        .padStart(2, "0")}:${date.getSeconds().toString().padStart(2, "0")}`;

      return `${formattedDate} ${formattedTime}`;
    } catch (error) {
      console.error("Error formatting timestamp:", error);
      return "-";
    }
  };

  // Function to process the file system recursively
  const processFileSystem = async (fileSystem, teamStats, userId) => {
    await Promise.all(
      fileSystem.map(async (file) => {
        const {
          id: fileId,
          name: lessonName,
          fileSystem: nestedFileSystem,
        } = file;

        const courseItemDoc = await db
          .collection("teamsCourseItems")
          .doc("" + fileId)
          .get();

        if (courseItemDoc.exists) {
          const courseItem = courseItemDoc.data();

          let hasInitializedIDE = false;
          let lastSeenTotal = null;

          const getIDEInitializedStatus = async (ideId) => {
            let isInitialized = false;
            const teamsUserProjectsDoc = await db
              .collection("teamsUserProjects")
              .where("userId", "==", userId)
              .where("teamProjectId", "==", ideId)
              .get();

            let lastSeen = null;

            if (teamsUserProjectsDoc.docs.length > 0) {
              const teamUserProject = teamsUserProjectsDoc.docs[0].data();

              isInitialized = true;
              lastSeen = teamUserProject.createdAt;
            }

            return { isInitialized, lastSeen };
          };

          const getLessonContentSubmissionStatus = async (courseItem) => {
            let submitted = false;

            // Check if the user has submitted any component of the lesson content
            const content = JSON.parse(courseItem.content)?.content || [];
            if (!Array.isArray(content)) {
              return {
                submitted: false,
                lastSeen: null,
              };
            }

            for (const item of content) {
              const itemId = item?.attrs?.id || item?.attrs?.projectId;

              if (!itemId) {
                break;
              }
              const teamsQuestionSubmissionsSnapshot = await db
                .collection("teamsQuestionSubmissions")
                .where("userId", "==", userId)
                .where("questionId", "==", itemId)
                .get();

              if (!teamsQuestionSubmissionsSnapshot.empty) {
                return {
                  submitted: true,
                  lastSeen:
                    teamsQuestionSubmissionsSnapshot.docs[0].data().createdAt,
                };
              }

              if (item?.attrs?.projectId) {
                const { isInitialized, lastSeen } =
                  await getIDEInitializedStatus(item?.attrs?.projectId);
                if (isInitialized) {
                  return {
                    submitted: true,
                    lastSeen,
                  };
                }
              }

              if (item?.type === "tabs") {
                for (const tab of item?.attrs?.tabs || []) {
                  return getLessonContentSubmissionStatus({
                    ...courseItem,
                    content: tab?.content?.content || [],
                  });
                }
              }

              if (submitted) break;
            }

            return { submitted };
          };

          if (courseItem.type === "coding-assignment") {
            const { isInitialized, lastSeen: lastSeenIDE } =
              await getIDEInitializedStatus(courseItem.id);

            hasInitializedIDE = isInitialized;
            lastSeenTotal = lastSeenIDE;
          }

          let hasSubmittedContent = false;
          if (courseItem.type === "lesson") {
            const { submitted, lastSeen: lastSeenLesson } =
              await getLessonContentSubmissionStatus(courseItem);

            hasSubmittedContent = submitted;
            lastSeenTotal = lastSeenLesson;
          }

          if (hasSubmittedContent || hasInitializedIDE) {
            const existingStat = teamStats?.find(
              (stat) => stat.lessonName === lessonName
            );

            if (existingStat) {
              existingStat.studentSubmissions += 1;
              if (
                lastSeenTotal &&
                (!existingStat.lastSeen ||
                  new Date(lastSeenTotal) > new Date(existingStat.lastSeen))
              ) {
                existingStat.lastSeen = lastSeenTotal;
              }
            } else {
              teamStats.push({
                lessonName,
                studentSubmissions: 1,
                lastSeen: lastSeenTotal,
              });
            }
          }
        } else {
          // console.log("course item doc doesnt exist", fileId);
        }

        if (nestedFileSystem) {
          await processFileSystem(nestedFileSystem, teamStats, userId);
        }
      })
    );
  };

  // Function to process team stats
  const processTeamStatsData = async (team, templatesStats, teacherId) => {
    const fileSystem = parseFileSystem(team.fileSystem);
    const teamStats = [];

    const teamMembers = Object.keys(team.teamMembers || {}).filter(
      (memberId) => memberId !== teacherId
    );

    // await Promise.all(
    let count = 0;
    for (const userId of teamMembers || []) {
      count += 1;

      await processFileSystem(fileSystem, teamStats, userId);

      console.log(
        "finished processing file system",
        count,
        "/",
        teamMembers.length,
        teamStats
      );
    }

    templatesStats.push({
      name: team.name,
      templateName: team?.forkedFromCourse,
      isJMCourse: !!team?.forkedFromCourse,
      lessonStats: teamStats,
    });

    console.log("templatesStats", team.name);
  };

  // Function to stringify template stats
  const stringifyTemplateStats = (templatesStats) => {
    if (!templatesStats || !templatesStats.length) {
      return "";
    }

    const renderLessonStats = (stats) => {
      return stats
        .map(
          (stat) =>
            `{${stat.lessonName}, ${stat.studentSubmissions || 0}, ${
              stat.lastSeen ? formatTimestamp(stat.lastSeen) : "-"
            }}`
        )
        .filter((x) => x)
        .join(",\n");
    };

    return templatesStats
      .map((stat) => {
        if (stat.lessonStats) {
          // not sure why we're filtering out lastSeen
          // stat.lessonStats = stat.lessonStats.filter((stat) => stat?.lastSeen);
        }
        if (!stat?.lessonStats?.length) return "";

        return `{${stat.name} - (${
          stat.forkedFromCourse
            ? `JM_COURSE - ${stat.forkedFromCourse}`
            : "CUSTOM"
        }) -> [${renderLessonStats(stat.lessonStats)}]}`;
      })
      .filter((x) => x)
      .join(",\n");
  };

  const stringifyNewTemplateStats = (templatesStats) => {
    if (!templatesStats || typeof templatesStats !== "object") {
      return "";
    }

    const renderTeamItemStats = (stats) => {
      // Convert object values to array if stats is an object
      const statsArray =
        typeof stats === "object" && !Array.isArray(stats)
          ? Object.values(stats)
          : stats;

      return statsArray
        .map(
          (stat) =>
            `{${stat.name}, ${getTotalMembersWithUsage(stat) || 0}, ${
              stat.latestUsageTimestamp
                ? formatTimestamp(stat.latestUsageTimestamp, true)
                : "-"
            }, ${JSON.stringify(stat.codingLanguage)}, AIGrading: ${
              stat.isAiGrading ? "Yes" : "No"
            }, ALaCarte: ${
              stat.aLaCarteCourse ? stat.aLaCarteCourse : "No"
            }, ALaCarteActivityId: ${
              stat.aLaCarteActivityId ? stat.aLaCarteActivityId : "No"
            }}`
        )
        .filter((x) => x)
        .join(",\n");
    };

    // Convert templatesStats object to array
    const templatesStatsArray = Object.values(templatesStats);

    return templatesStatsArray
      .map((stat) => {
        if (!stat?.items) return "";

        return `{${stat.name} - (${
          stat.forkedFromCourse
            ? `JM_COURSE - ${stat.forkedFromCourse}`
            : "CUSTOM"
        }) -> [${renderTeamItemStats(stat.items)}]}`;
      })
      .filter((x) => x)
      .join(",\n");
  };

  // Function to check if a user is active in teams
  const isUserActiveInTeams = (teamStats) => {
    if (!teamStats || typeof teamStats !== "object") return "No";

    const currentDate = new Date();
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(currentDate.getMonth() - 1);

    // For comparison with timestamps in milliseconds
    const oneMonthAgoMs = oneMonthAgo.getTime();

    // Convert teamStats to array if it's an object
    const teamStatsArray = Object.values(teamStats);

    // Count total items across all teams
    let totalTeamItems = 0;
    let recentActivity = false;

    for (const team of teamStatsArray) {
      // Add items count for this team (if items property exists)
      if (team.items && typeof team.items === "object") {
        const itemsArray = Object.values(team.items);
        totalTeamItems += itemsArray.length;

        // Check each item for recent activity
        for (const item of itemsArray) {
          if (item.latestUsageTimestamp) {
            let timestampMs;

            // Handle different timestamp formats
            if (typeof item.latestUsageTimestamp === "number") {
              // It's already a millisecond timestamp
              timestampMs = item.latestUsageTimestamp;
            } else if (item.latestUsageTimestamp.seconds) {
              // It's a Firestore timestamp
              timestampMs =
                item.latestUsageTimestamp.seconds * 1000 +
                (item.latestUsageTimestamp.nanoseconds
                  ? Math.floor(item.latestUsageTimestamp.nanoseconds / 1e6)
                  : 0);
            } else if (item.latestUsageTimestamp.toDate) {
              // It's a Firestore timestamp with toDate method
              timestampMs = item.latestUsageTimestamp.toDate().getTime();
            } else {
              // Try to parse as date string
              try {
                timestampMs = new Date(item.latestUsageTimestamp).getTime();
              } catch (e) {
                console.error(
                  "Couldn't parse timestamp:",
                  item.latestUsageTimestamp
                );
                continue; // Skip this item if we can't parse the timestamp
              }
            }

            // Compare with one month ago
            if (timestampMs > oneMonthAgoMs) {
              recentActivity = true;
              break;
            }
          }
        }

        // If we found recent activity, no need to check more teams
        if (recentActivity) break;
      }
    }

    // Check if there are 4 or more team items in total
    if (totalTeamItems < 4) return "No";

    return recentActivity ? "Yes" : "No";
  };

  // Function to check if a user has active curriculum
  const isUserActiveCurriculum = (teamStats) => {
    if (!teamStats || typeof teamStats !== "object") return "No";

    const currentDate = new Date();
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(currentDate.getMonth() - 1);

    // For comparison with timestamps in milliseconds
    const oneMonthAgoMs = oneMonthAgo.getTime();

    // Convert teamStats to array if it's an object
    const teamStatsArray = Object.values(teamStats);

    // Variables to track our criteria
    let hasCurriculum = false;
    let hasRecentActivity = false;

    for (const team of teamStatsArray) {
      // Check if the team itself is from a course (forkedFromCourse)
      const isTeamFromCourse = !!team.forkedFromCourse;

      // Check if team has items
      if (team.items && typeof team.items === "object") {
        const itemsArray = Object.values(team.items);

        // Check each item for curriculum and recent activity
        for (const item of itemsArray) {
          // Check if this is a curriculum item (has ALaCarte value or is from a course)
          // If the team is forked from a course, all its items count as curriculum
          if (
            (item.aLaCarteCourse && item.aLaCarteCourse !== "No") ||
            isTeamFromCourse
          ) {
            hasCurriculum = true;

            // If we found curriculum, check for recent activity
            if (item.latestUsageTimestamp) {
              let timestampMs;

              // Handle different timestamp formats (same as in isUserActiveInTeams)
              if (typeof item.latestUsageTimestamp === "number") {
                timestampMs = item.latestUsageTimestamp;
              } else if (item.latestUsageTimestamp.seconds) {
                timestampMs =
                  item.latestUsageTimestamp.seconds * 1000 +
                  (item.latestUsageTimestamp.nanoseconds
                    ? Math.floor(item.latestUsageTimestamp.nanoseconds / 1e6)
                    : 0);
              } else if (item.latestUsageTimestamp.toDate) {
                timestampMs = item.latestUsageTimestamp.toDate().getTime();
              } else {
                try {
                  timestampMs = new Date(item.latestUsageTimestamp).getTime();
                } catch (e) {
                  console.error(
                    "Couldn't parse timestamp:",
                    item.latestUsageTimestamp
                  );
                  continue;
                }
              }

              // Check if the activity was recent
              if (timestampMs > oneMonthAgoMs) {
                hasRecentActivity = true;
                break;
              }
            }
          }
        }

        // If we already found what we need, no need to check more teams
        if (hasCurriculum && hasRecentActivity) break;
      }
    }

    // Only return "Yes" if both conditions are met
    return hasCurriculum && hasRecentActivity ? "Yes" : "No";
  };

  // Main function to get team data
  const getTeamData = async (userId) => {
    try {
      const storedTeamData = await db
        .collection("userTeamsUsage")
        .doc(userId)
        .get();
      if (storedTeamData.exists) {
        const stats = storedTeamData.data().teams;
        const statsArray = Object.values(stats);
        const totalReplitImports = statsArray.filter(
          (stat) => stat.isReplit
        ).length;
        const totalCodingroomImports = statsArray.filter(
          (stat) => stat.isCodingRooms
        ).length;
        const totalTemplatesUsed = statsArray.filter(
          (stat) => !!stat.forkedFromCourse
        ).length;
        if (stats) {
          return {
            stats: stringifyNewTemplateStats(stats),
            totalReplitImports,
            totalCodingroomImports,
            totalTemplatesUsed,
            activeInTeams: isUserActiveInTeams(stats),
            activeCurriculum: isUserActiveCurriculum(stats),
          };
        }
        return storedTeamData.data();
      }

      // const templatesStats = await updateUserTeamsUsage(userId);
      // console.log("templatesStats", templatesStats);
      // const totalReplitImports = templatesStats.stats.filter(
      //   (stat) => stat.isReplit
      // ).length;
      // const totalCodingroomImports = templatesStats.stats.filter(
      //   (stat) => stat.isCodingRooms
      // ).length;
      // const totalTemplatesUsed = templatesStats.stats.filter(
      //   (stat) => !!stat.forkedFromCourse
      // ).length;
      // const returnValue = {
      //   totalReplitImports: totalReplitImports,
      //   totalCodingroomImports: totalCodingroomImports,
      //   totalTemplatesUsed: totalTemplatesUsed,
      //   stats:
      //     stringifyTemplateStats(
      //       JSON.parse(
      //         JSON.stringify(templatesStats.stats && templatesStats.stats)
      //       )
      //     ) || "-",
      // };
      // console.log("Saved successfully!", userId);
      // return returnValue;
    } catch (error) {
      console.error(error);
    }
  };

  const fetchData = async (search = "") => {
    setLoading(true);
    let query = db
      .collection("users")
      .where("isTeacher", "==", true)
      .orderBy("createdAt", "desc")
      .limit(50);

    // Check if there's a search term for the email
    if (search.trim() !== "") {
      // Adjust the query to filter by exact email match
      query = db
        .collection("users")
        .where("isTeacher", "==", true)
        .where("email", "==", search.trim());
      console.log("email ==", search.trim());
    } else if (lastVisible) {
      // Apply pagination for non-search queries
      query = query.startAfter(lastVisible);
    }

    query
      .get()
      .then(async (querySnapshot) => {
        if (querySnapshot.docs.length < 50) {
          setHasMore(false); // If fewer documents than the limit are returned, no more data is available
        } else {
          setHasMore(true);
        }
        const lastVisibleDoc =
          querySnapshot.docs[querySnapshot.docs.length - 1];
        setLastVisible(lastVisibleDoc);

        const items = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        const itemsWithTeamsDataPromises = [];
        items.forEach((userData) => {
          itemsWithTeamsDataPromises.push(
            getTeamData(userData.id).then((teamData) => {
              console.log("teamData", userData.id, teamData);
              return { ...userData, ...teamData };
            })
          );
        });

        const itemsWithTeamsData = await Promise.all(
          itemsWithTeamsDataPromises
        );
        if (search.trim() !== "") {
          // For searches, replace the current users list with the new results
          setUsers(itemsWithTeamsData);
        } else {
          // For pagination, append the new results
          setUsers((prev) => [...prev, ...itemsWithTeamsData]);
        }
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching data: ", error);
        setLoading(false);
      });
  };

  useEffect(() => {
    const main = async () => {
      setUsers([]);
      fetchData(searchQuery || "");
    };
    main();
  }, [searchQuery]);

  const handleEmailOpenStatusChange = async (email, value) => {
    try {
      // Find the user with this email and update their document
      const userQuery = await db
        .collection("users")
        .where("email", "==", email)
        .get();
      if (!userQuery.empty) {
        const userDoc = userQuery.docs[0];
        await db
          .collection("users")
          .doc(userDoc.id)
          .update({
            openedByRyan: value === "Yes",
          });

        setUsers((prevUsers) =>
          prevUsers.map((user) => {
            if (user.email === email) {
              return {
                ...user,
                openedByRyan: value === "Yes",
              };
            }
            return user;
          })
        );
      }
    } catch (error) {
      console.error("Error updating email status:", error);
    }
  };

  const getUsersDataArray = useCallback(
    (items) => {
      return items.map((row) => {
        const emailDomain = row.email.split("@")[1];

        const matchingDomain = businessData.find(
          (mapping) => mapping.id === emailDomain
        );

        const onUpdateStateAndDistrict = ({ state, district }) => {
          setBusinessData((prev) => {
            return prev.map((mapping) => {
              if (mapping.id === emailDomain) {
                return {
                  ...mapping,
                  state,
                  district,
                };
              }
              return mapping;
            });
          });
        };

        return [
          {
            id: row.id,
            onClick: () => history.push("/quizzes/user/" + row.id),
            text: row?.name ? splitName(row?.name)?.firstName : "NO NAME",
            ...(matchingDomain?.state || matchingDomain?.district
              ? {
                  state: matchingDomain?.state || "",
                  district: matchingDomain?.district || "",
                  onUpdateStateAndDistrict: onUpdateStateAndDistrict,
                }
              : {}),
          },
          {
            text: row?.name ? splitName(row?.name)?.lastName : "NO NAME",
          },
          { isCopyable: true, text: row.email },
          { isCopyable: false, text: row?.subscriptionValue || "-" },
          { isCopyable: false, text: row?.poc || "-" },
          ...(matchingDomain?.state && matchingDomain?.district
            ? [
                {
                  text: matchingDomain?.state,
                  isState: false,
                  userId: row.id,
                },
                { text: matchingDomain?.district },
              ]
            : [
                {
                  text: matchingDomain?.state || null,
                  isState: !matchingDomain?.district && !matchingDomain?.state,
                  userId: row?.id,
                },
                {
                  text: matchingDomain?.district || null,
                  userId: row?.id,
                },
              ]),
          {
            text: row.answered ? "Yes" : "No",
          },
          {
            text: row.adminPOC || "-",
            textField: {
              value: row.adminPOC || "",
              onChange: async (evt) => {
                const newValue = evt.target.value;
                await db.collection("users").doc(row.id).update({
                  adminPOC: newValue,
                });
                setUsers((prevState) =>
                  prevState.map((user) =>
                    user.id === row.id ? { ...user, adminPOC: newValue } : user
                  )
                );
              },
            },
          },
          {
            text: row.adminEmail || "-",
            textField: {
              value: row.adminEmail || "",
              onChange: async (evt) => {
                const newValue = evt.target.value;
                await db.collection("users").doc(row.id).update({
                  adminEmail: newValue,
                });
                setUsers((prevState) =>
                  prevState.map((user) =>
                    user.id === row.id
                      ? { ...user, adminEmail: newValue }
                      : user
                  )
                );
              },
            },
          },
          {
            text: row.firstContactForRenewal || "-",
            textField: {
              value: row.firstContactForRenewal || "",
              onChange: async (evt) => {
                const newValue = evt.target.value;
                await db.collection("users").doc(row.id).update({
                  firstContactForRenewal: newValue,
                });
                setUsers((prevState) =>
                  prevState.map((user) =>
                    user.id === row.id
                      ? { ...user, firstContactForRenewal: newValue }
                      : user
                  )
                );
              },
            },
          },
          {
            text: row.planType || "-",
            selector: {
              options: [
                { value: "District", text: "District" },
                { value: "Individual teacher", text: "Individual teacher" },
              ],
              onChange: async (evt) => {
                await db.collection("users").doc(row.id).update({
                  planType: evt.target.value,
                });
                setUsers((prevState) =>
                  prevState.map((user) =>
                    user.id === row.id
                      ? { ...user, planType: evt.target.value }
                      : user
                  )
                );
              },
            },
          },
          {
            text: row.numSchools || "-",
            textField: {
              value: row.numSchools || "",
              onChange: async (evt) => {
                const newValue = evt.target.value;
                await db.collection("users").doc(row.id).update({
                  numSchools: newValue,
                });
                setUsers((prevState) =>
                  prevState.map((user) =>
                    user.id === row.id
                      ? { ...user, numSchools: newValue }
                      : user
                  )
                );
              },
            },
          },
          {
            text: row.subType2023_2024 || "-",
            textField: {
              value: row.subType2023_2024 || "",
              onChange: async (evt) => {
                const newValue = evt.target.value;
                await db.collection("users").doc(row.id).update({
                  subType2023_2024: newValue,
                });
                setUsers((prevState) =>
                  prevState.map((user) =>
                    user.id === row.id
                      ? { ...user, subType2023_2024: newValue }
                      : user
                  )
                );
              },
            },
          },
          {
            text: row.paymentType2023_2024 || "-",
            textField: {
              value: row.paymentType2023_2024 || "",
              onChange: async (evt) => {
                const newValue = evt.target.value;
                await db.collection("users").doc(row.id).update({
                  paymentType2023_2024: newValue,
                });
                setUsers((prevState) =>
                  prevState.map((user) =>
                    user.id === row.id
                      ? { ...user, paymentType2023_2024: newValue }
                      : user
                  )
                );
              },
            },
          },
          {
            text: row.recurrence2023_2024 || "-",
            textField: {
              value: row.recurrence2023_2024 || "",
              onChange: async (evt) => {
                const newValue = evt.target.value;
                await db.collection("users").doc(row.id).update({
                  recurrence2023_2024: newValue,
                });
                setUsers((prevState) =>
                  prevState.map((user) =>
                    user.id === row.id
                      ? { ...user, recurrence2023_2024: newValue }
                      : user
                  )
                );
              },
            },
          },
          {
            text: row.amountPaid2023_2024 || "-",
            textField: {
              value: row.amountPaid2023_2024 || "",
              onChange: async (evt) => {
                const newValue = evt.target.value;
                await db.collection("users").doc(row.id).update({
                  amountPaid2023_2024: newValue,
                });
                setUsers((prevState) =>
                  prevState.map((user) =>
                    user.id === row.id
                      ? { ...user, amountPaid2023_2024: newValue }
                      : user
                  )
                );
              },
            },
          },
          {
            text: row.expiryDate2023_2024 || "-",
            textField: {
              value: row.expiryDate2023_2024 || "",
              onChange: async (evt) => {
                const newValue = evt.target.value;
                await db.collection("users").doc(row.id).update({
                  expiryDate2023_2024: newValue,
                });
                setUsers((prevState) =>
                  prevState.map((user) =>
                    user.id === row.id
                      ? { ...user, expiryDate2023_2024: newValue }
                      : user
                  )
                );
              },
            },
          },
          {
            text: row.renewed ? "Yes" : "No",
            selector: {
              options: [
                { value: "No", text: "No" },
                { value: "Yes", text: "Yes" },
              ],
              onChange: async (evt) => {
                const newValue = evt.target.value === "Yes";
                await db.collection("users").doc(row.id).update({
                  renewed: newValue,
                });
                setUsers((prevState) =>
                  prevState.map((user) =>
                    user.id === row.id ? { ...user, renewed: newValue } : user
                  )
                );
              },
            },
          },
          {
            text: row.subType2024_2025 || "-",
            textField: {
              value: row.subType2024_2025 || "",
              onChange: async (evt) => {
                const newValue = evt.target.value;
                await db.collection("users").doc(row.id).update({
                  subType2024_2025: newValue,
                });
                setUsers((prevState) =>
                  prevState.map((user) =>
                    user.id === row.id
                      ? { ...user, subType2024_2025: newValue }
                      : user
                  )
                );
              },
            },
          },
          {
            text: row.paymentType2024_2025 || "-",
            textField: {
              value: row.paymentType2024_2025 || "",
              onChange: async (evt) => {
                const newValue = evt.target.value;
                await db.collection("users").doc(row.id).update({
                  paymentType2024_2025: newValue,
                });
                setUsers((prevState) =>
                  prevState.map((user) =>
                    user.id === row.id
                      ? { ...user, paymentType2024_2025: newValue }
                      : user
                  )
                );
              },
            },
          },
          {
            text: row.recurrence2024_2025 || "-",
            textField: {
              value: row.recurrence2024_2025 || "",
              onChange: async (evt) => {
                const newValue = evt.target.value;
                await db.collection("users").doc(row.id).update({
                  recurrence2024_2025: newValue,
                });
                setUsers((prevState) =>
                  prevState.map((user) =>
                    user.id === row.id
                      ? { ...user, recurrence2024_2025: newValue }
                      : user
                  )
                );
              },
            },
          },
          {
            text: row.amountPaid2024_2025 || "-",
            textField: {
              value: row.amountPaid2024_2025 || "",
              onChange: async (evt) => {
                const newValue = evt.target.value;
                await db.collection("users").doc(row.id).update({
                  amountPaid2024_2025: newValue,
                });
                setUsers((prevState) =>
                  prevState.map((user) =>
                    user.id === row.id
                      ? { ...user, amountPaid2024_2025: newValue }
                      : user
                  )
                );
              },
            },
          },
          {
            text: row.expiryDate2024_2025 || "-",
            textField: {
              value: row.expiryDate2024_2025 || "",
              onChange: async (evt) => {
                const newValue = evt.target.value;
                await db.collection("users").doc(row.id).update({
                  expiryDate2024_2025: newValue,
                });
                setUsers((prevState) =>
                  prevState.map((user) =>
                    user.id === row.id
                      ? { ...user, expiryDate2024_2025: newValue }
                      : user
                  )
                );
              },
            },
          },
          {
            text: row.fromSiena ? "Yes" : "No",
            selector: {
              options: [
                { value: "No", text: "No" },
                { value: "Yes", text: "Yes" },
              ],
              onChange: async (evt) => {
                const newValue = evt.target.value === "Yes";
                await db.collection("users").doc(row.id).update({
                  fromSiena: newValue,
                });
                setUsers((prevState) =>
                  prevState.map((user) =>
                    user.id === row.id ? { ...user, fromSiena: newValue } : user
                  )
                );
              },
            },
          },
          {
            text: row.notes || "-",
            textField: {
              value: row.notes || "",
              onChange: async (evt) => {
                const newValue = evt.target.value;
                await db.collection("users").doc(row.id).update({
                  notes: newValue,
                });
                setUsers((prevState) =>
                  prevState.map((user) =>
                    user.id === row.id ? { ...user, notes: newValue } : user
                  )
                );
              },
            },
          },
          {
            text:
              row.emailIsReal != null
                ? !row.emailIsReal
                : row.neverBounceResponse == "invalid",
            onChange: async (newVal) => {
              const emailIsReal = !newVal;
              console.log("row.id", emailIsReal);
              await db.collection("users").doc(row.id).update({
                emailIsReal: emailIsReal,
              });

              console.log("emailIsReal", emailIsReal);

              setUsers(
                users.map((userPrevState) => {
                  if (userPrevState.id === row.id) {
                    console.log("user chosed", userPrevState);
                    let newObj = {
                      ...userPrevState,
                      emailIsReal: emailIsReal,
                    };
                    console.log("newObj", newObj);
                    return newObj;
                  }
                  return userPrevState;
                })
              );
            },
          },
          {
            text:
              row.emailIsReal != null && !row.emailIsReal
                ? "invalid"
                : row.neverBounceResponse,
          },
          { text: row.ownsATeam ? "Yes" : "No" },
          { text: row.hasCreatedATeamProject ? "Yes" : "No" },
          { text: row.totalTeamMembersAdded || 0 },
          { text: row.totalReplitImports },
          { text: row.totalCodingroomImports },
          { text: row.totalTemplatesUsed },
          { text: row.stats || "-" },
          { text: row.activeInTeams || "No" },
          { text: row.activeCurriculum || "No" },
          { text: row.totalQuizGamesStarted },
          { text: row.totalCustomQuizGamesStarted },
          { text: row.totalAssignedQuizGamesStarted },
          {
            text:
              row.quizzesStartedNames &&
              JSON.stringify(row.quizzesStartedNames),
          },
          {
            text:
              row.quizzesStartedCourses &&
              JSON.stringify(row.quizzesStartedCourses),
          },
          {
            text:
              row.viewProductOrStartTutorialsClickedTime ||
              row.learnMoreAboutProductClickedTime
                ? "Yes"
                : "No",
          },
          {
            text: row.viewProductOrStartTutorialsProjectId
              ? row.viewProductOrStartTutorialsProjectId
              : row.learnMoreAboutProductProjectId
              ? row.learnMoreAboutProductProjectId
              : "",
          },
          {
            text: row.lastSeen
              ? row.lastSeen?.toDate()?.toLocaleString()
              : row.createdAt &&
                row.createdAt?.toDate() &&
                row.createdAt?.toDate()?.toLocaleString()
              ? row.createdAt?.toDate()?.toLocaleString()
              : "",
          },
          {
            text: row.permanentSubscription
              ? row.permanentSubscription
              : row.subscriptionStatus
              ? row.subscriptionStatus
              : row.stripeId
              ? "Clicked trial"
              : "Not subscribed",
            selector: {
              options: [
                {
                  value: row.subscriptionStatus
                    ? row.subscriptionStatus
                    : row.stripeId
                    ? "Clicked trial"
                    : "Not subscribed",
                  text: row.subscriptionStatus
                    ? row.subscriptionStatus
                    : row.stripeId
                    ? "Clicked trial"
                    : "Not subscribed",
                },
                { value: "Starter", text: "Starter" },
                { value: "Silver", text: "Silver" },
                { value: "Gold", text: "Gold" },
                { value: "Diamond", text: "Diamond" },
              ],
              onChange: async (evt) => {
                const newValue = evt.target.value;

                let finalPermValue = null;

                if (
                  newValue == "Gold" ||
                  newValue == "Silver" ||
                  newValue == "Starter" ||
                  newValue == "Diamond"
                ) {
                  finalPermValue = newValue;
                }

                await db.collection("users").doc(row.id).update({
                  permanentSubscription: finalPermValue,
                });

                setUsers((prevState) => {
                  return prevState.map((user) => {
                    if (user.id === row.id) {
                      // Check if this is the object we want to update
                      return {
                        ...user, // Copy all existing user properties
                        permanentSubscription: finalPermValue, // Update the specific field
                      };
                    } else {
                      return user; // Return the unchanged object
                    }
                  });
                });
              },
            },
          },
          {
            text: row.openedByRyan ? "Yes" : "No",
            onChange: async (newVal) => {
              await handleEmailOpenStatusChange(row.email, newVal);
            },
            selector: {
              options: [
                { value: "No", text: "No" },
                { value: "Yes", text: "Yes" },
              ],
              onChange: async (evt) => {
                await handleEmailOpenStatusChange(row.email, evt.target.value);
              },
            },
          },
          {
            text: row.neverContacted ? "Yes" : "No",
            selector: {
              options: [
                { value: "No", text: "No" },
                { value: "Yes", text: "Yes" },
              ],
              onChange: async (evt) => {
                const newValue = evt.target.value === "Yes";
                await db.collection("users").doc(row.id).update({
                  neverContacted: newValue,
                });
                setUsers((prevState) =>
                  prevState.map((user) =>
                    user.id === row.id
                      ? { ...user, neverContacted: newValue }
                      : user
                  )
                );
              },
            },
          },
        ];
      });
    },
    [businessData, history, users]
  );

  const generateCSVData = async () => {
    try {
      console.log("querying the users");
      let query = db
        .collection("users")
        .where("isTeacher", "==", true)
        .orderBy("createdAt", "desc")
        .limit(500); // Start with a limit of 500

      console.log("done querying the users");

      const dashboardTeamsUsageSnapshot = await db
        .collection("userTeamsUsage")
        .get();

      const dashboardTeamsUsageStore = dashboardTeamsUsageSnapshot.docs.map(
        (doc) => ({
          id: doc.id,
          ...doc.data(),
        })
      );

      let allItems = [];
      let lastDoc = null;

      while (true) {
        console.log("processing next 500 documents");
        const querySnapshot = await query.get();
        console.log("done processing the 500", querySnapshot.empty);

        // Check if there are documents
        if (querySnapshot.empty) break;

        const items = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        allItems = [...allItems, ...items];

        // Get the last document to use as the starting point for the next batch
        lastDoc = querySnapshot.docs[querySnapshot.docs.length - 1];

        // Update the query to fetch the next batch
        query = db
          .collection("users")
          .where("isTeacher", "==", true)
          .orderBy("createdAt", "desc")
          .startAfter(lastDoc)
          .limit(500);
      }

      const batchSize = 1; // Limit concurrent requests to 50 at a time
      const itemsWithTeamsData = [];

      for (let i = 0; i < allItems.length; i += batchSize) {
        console.log("processing batch", i, "/", allItems.length);
        const batch = allItems.slice(i, i + batchSize);
        const batchPromises = batch.map(async (userData) => {
          const storedTeamData = dashboardTeamsUsageStore.find(
            (teamData) => teamData.id === userData.id
          );

          let totalReplitImports = "N/A";
          let totalCodingroomImports = "N/A";
          let totalTemplatesUsed = "N/A";
          let stats = "N/A";
          let activeInTeams = "No";
          let activeCurriculum = "No";

          if (storedTeamData && storedTeamData.teams) {
            const statsArray = Object.values(storedTeamData.teams);
            totalReplitImports = statsArray.filter(
              (stat) => stat.isReplit
            ).length;
            totalCodingroomImports = statsArray.filter(
              (stat) => stat.isCodingRooms
            ).length;
            totalTemplatesUsed = statsArray.filter(
              (stat) => !!stat.forkedFromCourse
            ).length;
            stats = stringifyNewTemplateStats(storedTeamData.teams);
            activeInTeams = isUserActiveInTeams(storedTeamData.teams);
            activeCurriculum = isUserActiveCurriculum(storedTeamData.teams);
          }

          const teamData = {
            totalReplitImports,
            totalCodingroomImports,
            totalTemplatesUsed,
            stats,
            activeInTeams,
            activeCurriculum,
          };

          return { ...userData, ...teamData };
        });
        // console.log("processing batch", i, allItems.length);
        const batchResults = await Promise.all(batchPromises); // Process one batch at a time
        itemsWithTeamsData.push(...batchResults);
      }

      console.log("about to process csvData");

      const csvData = [
        headers.slice(2),
        ...getUsersDataArray(itemsWithTeamsData).map((innerArray) =>
          innerArray.map((obj) => obj.text)
        ),
      ];

      console.log("csvData", csvData);

      return csvData;
    } catch (error) {
      setIsDownloadingCSV(false);
      console.error("Error generating CSV data:", error);
    }
  };

  const downloadCSV = async () => {
    setIsDownloadingCSV(true);
    const csvData = await generateCSVData();
    const csvContent = Papa.unparse(csvData);
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "table-data.csv");
    setIsDownloadingCSV(false);
  };

  const copyToClipboard = async (email) => {
    try {
      await navigator.clipboard.writeText(email);
      setShowCopySuccess(true);
    } catch (err) {
      setShowCopySuccess(false);
    }
  };

  const fetchBusinessName = async () => {
    const domainToSchoolDistrictAndStateMappingSnapshot = await db
      .collection("domainToSchoolDistrictAndStateMapping")
      .get();

    const domainToSchoolDistrictAndStateMapping =
      domainToSchoolDistrictAndStateMappingSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

    setBusinessData(domainToSchoolDistrictAndStateMapping);
  };

  useEffect(() => {
    fetchBusinessName();
  }, []);

  const testFunction = async () => {
    console.log("Calling dashboardUpdateUserTeamsUsage function");

    // get all teacher ids from users collection
    const teachersSnapshot = await db
      .collection("users")
      .where("isTeacher", "==", true)
      .get();
    const teacherIds = teachersSnapshot.docs.map((doc) => doc.id);
    // const teacherIds = ["B6W6IRFQwSRoBhA84bNbrRr7BCi1"];

    console.log("teacherIds", teacherIds);

    let successCount = 0;
    let errorCount = 0;
    let maxSizeCount = 0;

    // Process teachers in batches of 10
    for (let i = 0; i < teacherIds.length; i += 10) {
      const batch = teacherIds.slice(i, i + 10);

      await Promise.all(
        batch.map(async (teacherId) => {
          try {
            const result = await updateUserTeamsUsage(teacherId);
            console.log("teacherId", teacherId);
            console.log("result", result);
            if (result.status === "success") {
              successCount += 1;
            } else if (result.status === "max-size-exceeded") {
              maxSizeCount += 1;
            } else if (result.status === "error") {
              errorCount += 1;
            }
          } catch (error) {
            console.error(
              "Error calling dashboardUpdateUserTeamsUsage:",
              error
            );
            errorCount += 1;
          }
        })
      );

      console.log("Batch completed:", i / 10 + 1);
      console.log("successCount", successCount);
      console.log("errorCount", errorCount);
      console.log("maxSizeCount", maxSizeCount);
    }
  };

  return (
    <Paper sx={{ width: "100%", position: "relative" }}>
      <button
        style={{
          position: "absolute",
          top: "-54px",
          right: "180px",
          backgroundColor: showSubscriptions ? "blue" : "lightgrey",
          borderRadius: "10px",
          padding: "10px 20px",
          color: "white",
          cursor: "pointer",
          border: "4px solid rgba(0,0,0,.2)",
        }}
        onClick={() => setShowSubscriptions((prevState) => !prevState)}
      >
        Subscribers
      </button>
      <button
        style={{
          position: "absolute",
          top: "-54px",
          right: "330px",
          backgroundColor: "purple",
          borderRadius: "10px",
          padding: "10px 20px",
          color: "white",
          cursor: "pointer",
          border: "4px solid rgba(0,0,0,.2)",
        }}
        onClick={() => testFunction()}
      >
        Test function
      </button>
      <button
        style={{
          position: "absolute",
          top: "-54px",
          right: "30px",
          backgroundColor: "green",
          borderRadius: "10px",
          padding: "10px 20px",
          color: "white",
          cursor: "pointer",
          border: "4px solid rgba(0,0,0,.2)",
        }}
        onClick={() => downloadCSV()}
      >
        {isDownloadingCSV ? "Downloading..." : "Download CSV"}
      </button>

      <input
        placeholder="Search by Email"
        style={{
          width: "100%",
          border: "1px solid lightgrey",
          borderRadius: "10px",
          fontSize: "24px",
          padding: "10px",
        }}
        value={searchQuery}
        onChange={(e) => {
          setSearchQuery(e.target.value);
        }}
      />
      <TableContainer sx={{ maxHeight: 600 }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {headers.map((header) => (
                <TableCell>{header}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {getUsersDataArray(users).map((user) => {
              return (
                <TableRow
                  // onClick={() => history.push("/quizzes/user/" + user.id)}
                  className={styles.tableRow}
                  key={user.id}
                >
                  <TableCell>
                    {user.hasStartedQuiz ? (
                      <CheckCircle style={{ color: "green" }} />
                    ) : (
                      <DoNotDisturbOn style={{ color: "grey" }} />
                    )}
                  </TableCell>
                  <TableCell>
                    <EditUserButtonAlert
                      userRef={user[0]?.id}
                      firstName={user[0]?.text}
                      lastName={user[1]?.text}
                      userName={user.adminName ? user.adminName : user.name}
                      userEmail={user[2]?.text}
                      state={user[0]?.state}
                      district={user[0]?.district}
                      onUpdateStateAndDistrict={
                        user[0]?.onUpdateStateAndDistrict
                      }
                      subscriptionValue={
                        user[3]?.text === "-" || !user[3]?.text
                          ? ""
                          : user[3]?.text
                      }
                      poc={
                        user[4]?.text === "-" || !user[4]?.text
                          ? ""
                          : user[4]?.text
                      }
                      onFinish={fetchData}
                      onUpdate={(newName, newEmail) =>
                        setUsers((prevState) => {
                          return prevState.map((userPrevState) => {
                            if (userPrevState.id === user.id) {
                              return {
                                ...userPrevState,
                                adminName: newName,
                                adminEmail: newEmail,
                              };
                            }
                            return userPrevState;
                          });
                        })
                      }
                    />
                  </TableCell>
                  {user.map(
                    (
                      {
                        text,
                        onClick,
                        onChange,
                        isCopyable,
                        selector,
                        isState,
                        userId,
                        textField,
                      },
                      index
                    ) => (
                      <TableCell
                        onClick={
                          onClick
                            ? () => history.push("/quizzes/user/" + user.id)
                            : () => {}
                        }
                      >
                        {isState ? (
                          <AddBusinessButtonAlert
                            users={users}
                            userId={userId}
                            onFinish={() => {
                              fetchBusinessName();
                              fetchData();
                            }}
                          />
                        ) : null}
                        {headers[index + 2] == "Email bounced" ? (
                          <ControlledCheckboxRealEmail
                            value={text}
                            onChange={onChange}
                          />
                        ) : (
                          <>
                            {isCopyable && (
                              <IconButton
                                sx={{ marginRight: "10px" }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  copyToClipboard(text);
                                }}
                              >
                                <ContentCopy sx={{ fontSize: "20px" }} />
                              </IconButton>
                            )}

                            {selector ? (
                              <FormControl fullWidth size="small">
                                <Select
                                  value={text}
                                  onChange={selector.onChange}
                                >
                                  {selector.options.map(({ text, value }) => (
                                    <MenuItem value={value}>{text}</MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            ) : textField ? (
                              <TextField
                                size="small"
                                value={textField.value}
                                onChange={textField.onChange}
                                fullWidth
                                variant="outlined"
                              />
                            ) : (
                              <div
                                style={
                                  onClick
                                    ? {
                                        color: "blue",
                                        textDecoration: "unerline",
                                      }
                                    : {}
                                }
                              >
                                {text}
                              </div>
                            )}
                          </>
                        )}
                      </TableCell>
                    )
                  )}
                </TableRow>
              );
            })}
          </TableBody>
          {hasMore && (
            <button
              onClick={() => fetchData(searchQuery || "")}
              disabled={loading}
            >
              {loading ? "Loading..." : "Load More"}
            </button>
          )}
        </Table>
      </TableContainer>
      <Snackbar
        open={showCopySuccess}
        onClose={() => setShowCopySuccess(false)}
        autoHideDuration={2000}
        message="Copied email to clipboard"
      />
    </Paper>
  );
}
